import React, { useState, useEffect } from 'react';
import { Calendar, Filter, ChevronDown, X, MapPin, Loader2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../../styles/datepicker-custom.css';
import { format } from 'date-fns';
import { eventsService, State } from '../../services/eventsService';
import { toISTISOString } from '../../lib/utils';

interface EventFiltersProps {
    dateRange: '1h' | '24h' | '7d' | '30d' | 'custom';
    eventNameInput: string[];
    onDateRangeChange: (value: '1h' | '24h' | '7d' | '30d' | 'custom') => void;
    onEventNameChange: (value: string[]) => void;
    onCustomDateChange?: (fromDate: string, toDate: string) => void;
    onApplyFilters?: () => void;
    onClearFilters?: () => void;
    showEventNameFilter?: boolean;
    showStateFilter?: boolean;
    stateId?: number | null;
    onStateChange?: (stateId: number | null) => void;
    initialFromDate?: string;
    initialToDate?: string;
    isLoading?: boolean;
}

const EVENT_NAMES = [
    { value: 'EKYCDataStored', label: 'EKYCDataStored' },
    { value: 'EKYCDataUpdated', label: 'EKYCDataUpdated' },
    { value: 'JobCardDataStored', label: 'JobCardDataStored' },
    { value: 'JobCardDataUpdated', label: 'JobCardDataUpdated' },
    { value: 'MateAddressSet', label: 'MateAddressSet' },
    { value: 'PeriodAttendanceFinalized', label: 'PeriodAttendanceFinalized' },
];

export const EventFilters: React.FC<EventFiltersProps> = ({
    dateRange,
    eventNameInput,
    onDateRangeChange,
    onEventNameChange,
    onCustomDateChange,
    onApplyFilters,
    onClearFilters,
    showEventNameFilter = false,
    showStateFilter = false,
    stateId,
    onStateChange,
    initialFromDate,
    initialToDate,
    isLoading = false,
}) => {
    // Temporary state for filters (not applied until Apply is clicked)
    const [tempDateRange, setTempDateRange] = useState<'1h' | '24h' | '7d' | '30d' | 'custom'>('custom');
    const [tempEventNameInput, setTempEventNameInput] = useState<string[]>(eventNameInput);
    const [tempStateId, setTempStateId] = useState<number | null>(stateId || null);

    // Initialize dates from props or set to null
    const parseDate = (dateString?: string): Date | null => {
        if (!dateString) return null;
        try {
            return new Date(dateString);
        } catch {
            return null;
        }
    };

    const [fromDate, setFromDate] = useState<Date | null>(() => parseDate(initialFromDate));
    const [toDate, setToDate] = useState<Date | null>(() => parseDate(initialToDate));
    const [isEventDropdownOpen, setIsEventDropdownOpen] = useState(false);
    const [states, setStates] = useState<State[]>([]);
    const [loadingStates, setLoadingStates] = useState(false);

    // Sync temporary state when props change (e.g., after clear)
    useEffect(() => {
        setTempDateRange(dateRange);
        setTempEventNameInput(eventNameInput);
        if (showStateFilter) {
            setTempStateId(stateId || null);
        }
    }, [dateRange, eventNameInput, stateId, showStateFilter]);

    // Apply initial dates on mount if provided
    useEffect(() => {
        if (initialFromDate && initialToDate && onCustomDateChange) {
            onCustomDateChange(initialFromDate, initialToDate);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch states when showStateFilter is true
    useEffect(() => {
        if (showStateFilter) {
            const fetchStates = async () => {
                try {
                    setLoadingStates(true);
                    const statesData = await eventsService.getStates();
                    setStates(statesData);
                } catch (error) {
                    console.error('Error fetching states:', error);
                    setStates([]);
                } finally {
                    setLoadingStates(false);
                }
            };
            fetchStates();
        }
    }, [showStateFilter]);

    const handleDateRangeChange = (value: string) => {
        const newValue = value as '1h' | '24h' | '7d' | '30d' | 'custom';
        setTempDateRange(newValue);
    };

    const handleCustomDateApply = () => {
        if (fromDate && toDate && onCustomDateChange) {
            // Format dates as IST ISO 8601 strings
            const fromDateStr = toISTISOString(fromDate);
            const toDateStr = toISTISOString(toDate);
            onCustomDateChange(fromDateStr, toDateStr);
        }
    };

    const handleApplyFilters = () => {
        // Apply date range - always use custom since we're using date pickers
        onDateRangeChange('custom');

        // Apply event names
        onEventNameChange(tempEventNameInput);

        // Apply state filter
        if (showStateFilter && onStateChange) {
            onStateChange(tempStateId);
        }

        // Apply the custom dates
        if (fromDate && toDate && onCustomDateChange) {
            handleCustomDateApply();
        }

        // Trigger the apply callback
        if (onApplyFilters) {
            onApplyFilters();
        }
    };

    const handleClearFilters = () => {
        // Reset temporary state to defaults
        setTempDateRange('custom');
        setTempEventNameInput(['EKYCDataStored', 'EKYCDataUpdated', 'JobCardDataStored', 'JobCardDataUpdated', 'MateAddressSet', 'PeriodAttendanceFinalized']);
        setTempStateId(null);
        setFromDate(null);
        setToDate(null);

        // Apply the cleared values immediately
        onDateRangeChange('custom');
        onEventNameChange(['EKYCDataStored', 'EKYCDataUpdated', 'JobCardDataStored', 'JobCardDataUpdated', 'MateAddressSet', 'PeriodAttendanceFinalized']);
        if (showStateFilter && onStateChange) {
            onStateChange(null);
        }
        if (onCustomDateChange) {
            onCustomDateChange('', '');
        }

        // Trigger the clear callback
        if (onClearFilters) {
            onClearFilters();
        }
    };

    const toggleEventName = (eventValue: string) => {
        if (tempEventNameInput.includes(eventValue)) {
            setTempEventNameInput(tempEventNameInput.filter((v) => v !== eventValue));
        } else {
            setTempEventNameInput([...tempEventNameInput, eventValue]);
        }
    };

    const removeEventName = (eventValue: string) => {
        setTempEventNameInput(tempEventNameInput.filter((v) => v !== eventValue));
    };

    const clearAllEventNames = () => {
        setTempEventNameInput([]);
    };

    const getDisplayText = () => {
        if (tempEventNameInput.length === 0) {
            return 'No Events Selected';
        } else if (tempEventNameInput.length === 1) {
            return EVENT_NAMES.find((e) => e.value === tempEventNameInput[0])?.label || 'No Events Selected';
        } else {
            return `${tempEventNameInput.length} Events Selected`;
        }
    };

    return (
        <div className="flex flex-col gap-4">
            {/* First Row: Date Range, Event Name, and Action Buttons */}
            <div className="flex flex-col lg:flex-row gap-3 items-end">
                {/* Date Range Filter - Start Date */}
                <div className="w-full lg:w-[200px] flex-shrink-0">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Calendar className="w-4 h-4 inline mr-2" />
                        Date (Start)
                    </label>
                    <DatePicker
                        selected={fromDate}
                        onChange={(date: Date | null) => setFromDate(date)}
                        showTimeSelect
                        timeFormat="HH:mm"
                        timeIntervals={15}
                        dateFormat="dd/MM/yyyy HH:mm"
                        placeholderText="Select start date & time"
                        className="w-full h-10 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-brand focus:border-transparent"
                        maxDate={toDate || undefined}
                        popperPlacement="bottom-start"
                        popperClassName="datepicker-popper"
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                    />
                </div>

                {/* Date Range Filter - End Date */}
                <div className="w-full lg:w-[200px] flex-shrink-0">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date (End)</label>
                    <DatePicker
                        selected={toDate}
                        onChange={(date: Date | null) => setToDate(date)}
                        showTimeSelect
                        timeFormat="HH:mm"
                        timeIntervals={15}
                        dateFormat="dd/MM/yyyy HH:mm"
                        placeholderText="Select end date & time"
                        className="w-full h-10 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-brand focus:border-transparent"
                        minDate={fromDate || undefined}
                        popperPlacement="bottom-start"
                        popperClassName="datepicker-popper"
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                    />
                </div>

                {/* Event Name Filter */}
                {showEventNameFilter && (
                    <div className="w-full lg:w-[220px] flex-shrink-0 min-w-0">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <Filter className="w-4 h-4 inline mr-2" />
                            Event Name
                        </label>
                        <Popover open={isEventDropdownOpen} onOpenChange={setIsEventDropdownOpen}>
                            <PopoverTrigger asChild>
                                <Button variant="outline" className="w-full h-10 justify-between text-left font-normal min-w-0">
                                    <span className="truncate flex-1 min-w-0">{getDisplayText()}</span>
                                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[280px] p-0 bg-white border border-gray-200 rounded-md shadow-lg z-50" align="end" side="bottom" sideOffset={5} alignOffset={0}>
                                <div className="p-3 border-b bg-white">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-medium">Select Events</span>
                                        {tempEventNameInput.length > 0 && (
                                            <button onClick={clearAllEventNames} className="text-xs text-blue-600 hover:text-blue-700 font-medium">
                                                Clear All
                                            </button>
                                        )}
                                    </div>
                                    {tempEventNameInput.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mt-2">
                                            {tempEventNameInput.map((eventValue) => {
                                                const event = EVENT_NAMES.find((e) => e.value === eventValue);
                                                return (
                                                    <span key={eventValue} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-brand rounded text-xs">
                                                        {event?.label}
                                                        <button onClick={() => removeEventName(eventValue)} className="hover:text-blue-900">
                                                            <X className="h-3 w-3" />
                                                        </button>
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                <div className="max-h-[300px] overflow-y-auto bg-white">
                                    {EVENT_NAMES.map((event) => (
                                        <label key={event.value} className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer">
                                            <input type="checkbox" checked={tempEventNameInput.includes(event.value)} onChange={() => toggleEventName(event.value)} className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-brand" />
                                            <span className="text-sm">{event.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </PopoverContent>
                        </Popover>
                    </div>
                )}

                {/* State Filter */}
                {showStateFilter && (
                    <div className="w-full lg:w-[200px] flex-shrink-0">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <MapPin className="w-4 h-4 inline mr-2" />
                            State
                        </label>
                        <select value={tempStateId || ''} onChange={(e) => setTempStateId(e.target.value ? Number(e.target.value) : null)} className="w-full h-10 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-brand focus:border-transparent" disabled={loadingStates}>
                            <option value="">All States</option>
                            {states.map((state) => (
                                <option key={state.id} value={state.id}>
                                    {state.name}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {/* Apply and Clear Buttons */}
                <div className="flex flex-row gap-3 w-full lg:w-auto flex-shrink-0">
                    <Button onClick={handleApplyFilters} disabled={!fromDate || !toDate || (showEventNameFilter && tempEventNameInput.length === 0) || isLoading} className="h-10 bg-brand hover:bg-brand-hover text-white w-full lg:w-auto px-6">
                        {isLoading ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Applying...
                            </>
                        ) : (
                            'Apply Filter'
                        )}
                    </Button>
                    <Button onClick={handleClearFilters} variant="outline" className="h-10 w-full lg:w-auto px-6">
                        Clear
                    </Button>
                </div>
            </div>
        </div>
    );
};
